// @flow

import * as React from 'react'
import MomentUtils from '@date-io/moment'
import {
  Typography,
  Grid,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListSubheader,
  ListItemSecondaryAction,
  ListItemText,
  InputAdornment,
  IconButton,
} from '@material-ui/core'
import { ChevronRight, DateRange } from '@material-ui/icons'
import { MuiPickersUtilsProvider, DateTimePicker } from '@material-ui/pickers'
import { makeStyles } from '@material-ui/core/styles'

import { getHCMSForms } from 'services/forms'
import useAuthRequest from 'hooks/useAuthRequest'

import SubmissionHistoryResults from './SubmissionHistoryResults'
import NoForms from './components/NoForms'
import NoData from './components/NoData'
import moment from 'moment'

const useStyles = makeStyles((theme) => ({
  listSecondaryAction: {
    display: 'flex',
    color: theme.palette.text.secondary,
  },
  filters: { padding: theme.spacing(2) },
}))

type Props = {
  integration: IntegrationCPHCMS | null,
  formsAppEnvironmentId: number,
  formId: number | null,
  onSelectForm: (Form) => void,
  updateSearch: ({ key: string, value: mixed, persist: boolean }) => void,
  startDate: string,
  endDate: string,
}

const dateTimeFormat = 'MM/DD/YYYY hh:mm A'
const dateTimeEndAdornment = (
  <InputAdornment position="end">
    <IconButton>
      <DateRange />
    </IconButton>
  </InputAdornment>
)
function SubmissionHistoryFilters({
  integration,
  formId,
  formsAppEnvironmentId,
  onSelectForm,
  updateSearch,
  startDate,
  endDate,
}: Props) {
  const getHCMSFormsAuthenticated = useAuthRequest(getHCMSForms)
  const classes = useStyles()
  const [state, setState] = React.useState<{
    fetchError: Error | null,
    isFetching: boolean,
    forms: Form[],
  }>({
    fetchError: null,
    isFetching: true,
    forms: [],
  })
  const form = React.useMemo(() => {
    return state.forms.find((form) => form.id === formId)
  }, [formId, state.forms])

  React.useEffect(() => {
    setState({
      isFetching: true,
      fetchError: null,
      forms: [],
    })
    let ignore = false
    const fetchState = async () => {
      let newForms = []
      let newFetchError = null
      try {
        newForms = await getHCMSFormsAuthenticated(formsAppEnvironmentId)
      } catch (error) {
        newFetchError = error
      }
      if (!ignore) {
        setState({
          isFetching: false,
          fetchError: newFetchError,
          forms: newForms,
        })
      }
    }
    fetchState()

    return () => {
      ignore = true
    }
  }, [formsAppEnvironmentId, getHCMSFormsAuthenticated])

  if (state.fetchError) {
    return (
      <>
        <Typography align="center" variant="h2" color="error" paragraph>
          Error Loading Forms
        </Typography>
        <Typography align="center" paragraph>
          {state.fetchError.message}
        </Typography>
        <Typography align="center" color="textSecondary">
          Refresh to try again. If the problem persists, please contact support.
        </Typography>
      </>
    )
  }

  if (state.isFetching) {
    return (
      <Grid container justify="center">
        <Grid item>
          <CircularProgress />
        </Grid>
      </Grid>
    )
  }

  if (!state.forms.length) {
    return <NoForms />
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper className={classes.filters}>
          <MuiPickersUtilsProvider utils={MomentUtils}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <DateTimePicker
                  label="Submissions After"
                  value={moment(startDate)}
                  onChange={(val) => {
                    updateSearch({
                      key: 'startDate',
                      value: val.toISOString(),
                      persist: true,
                    })
                  }}
                  maxDate={endDate}
                  maxDateMessage='Must be before "Submissions Before"'
                  fullWidth
                  disableFuture
                  showTodayButton
                  autoOk
                  format={dateTimeFormat}
                  InputProps={{
                    endAdornment: dateTimeEndAdornment,
                  }}
                ></DateTimePicker>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <DateTimePicker
                  label="Submissions Before"
                  value={moment(endDate)}
                  onChange={(val) => {
                    updateSearch({
                      key: 'endDate',
                      value: val.toISOString(),
                      persist: true,
                    })
                  }}
                  minDate={startDate}
                  minDateMessage='Must be after "Submissions After"'
                  fullWidth
                  disableFuture
                  showTodayButton
                  autoOk
                  format={dateTimeFormat}
                  InputProps={{
                    endAdornment: dateTimeEndAdornment,
                  }}
                ></DateTimePicker>
              </Grid>
            </Grid>
          </MuiPickersUtilsProvider>
        </Paper>
      </Grid>
      <Grid item xs={12} sm={5} md={4}>
        <Paper>
          <List>
            <ListSubheader>FORMS</ListSubheader>
            {state.forms.map((form) => (
              <ListItem
                key={form.id}
                button
                selected={formId === form.id}
                onClick={() => onSelectForm(form)}
              >
                <ListItemText
                  primary={form.name}
                  secondary={form.description}
                ></ListItemText>
                <ListItemSecondaryAction
                  className={classes.listSecondaryAction}
                >
                  <ChevronRight />
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Paper>
      </Grid>
      <Grid item xs={12} sm={7} md={8}>
        {form && integration ? (
          <SubmissionHistoryResults
            integration={integration}
            form={form}
            startDate={startDate}
            endDate={endDate}
          />
        ) : (
          <NoData
            variant="form"
            text="No form selected, please select a form."
          />
        )}
      </Grid>
    </Grid>
  )
}

export default React.memo<Props>(SubmissionHistoryFilters)
